import ScrollModal from "@/components/common/scroll-modal";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetComplexRelationships } from "@/queries/n8n";
import {
  Button,
  Heading,
  Icon,
  TableData,
} from "@elaniintc/goes-design-system";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

interface Props {
  shouldFetch?: boolean;
  expedient_base_entity_id: string;
  expedient_base_registry_id: string;
}

const ComplexRelationships = ({
  expedient_base_entity_id,
  expedient_base_registry_id,
  shouldFetch,
}: Props) => {
  const { data, isLoading } = useGetComplexRelationships(
    expedient_base_entity_id,
    expedient_base_registry_id,
    shouldFetch,
  );

  if (!shouldFetch) {
    return <></>;
  }

  if (isLoading) {
    return (
      <div className="flex gap-2">
        <Skeleton className="h-8 w-[120px]" />
        <Skeleton className="h-8 w-[120px]" />
        <Skeleton className="h-8 w-[120px]" />
      </div>
    );
  }

  return (
    <div>
      {Object.keys(data || {}).map((key, index) => {
        const relationships = data ? data[key] : [];
        return (
          <ComplexRelationshipsModal
            entity={key}
            data={relationships}
            key={`${key}-${index}`}
          />
        );
      })}
    </div>
  );
};

export default ComplexRelationships;

interface ModalProps {
  entity: string;
  data?: Array<{
    [key: string]: string | number;
  }>;
}

const ComplexRelationshipsModal = ({ data, entity }: ModalProps) => {
  const [open, setOpen] = useState(false);

  const columns: ColumnDef<Record<string, unknown>>[] = useMemo(() => {
    if (!data || data.length === 0 || !data[0] || typeof data[0] !== "object")
      return [];
    const headers = Object.keys(data[0]).filter(
      (key) => !["id"].includes(key.toLowerCase()),
    );
    return headers.map((header) => ({
      accessorKey: header,
      header: header,
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = getValue();
        if (typeof value === "boolean") {
          return value ? "Sí" : "No";
        }
        if (value === null || value === undefined) {
          return "-";
        }
        return String(value);
      },
    }));
  }, [data]);

  return (
    <>
      <Button
        variant="tertiary"
        size="xs"
        type="button"
        onClick={() => setOpen(true)}
      >
        {entity}
      </Button>
      <ScrollModal
        title="Relaciones complejas"
        isOpen={open}
        onClose={() => setOpen(false)}
        modalContentClassName="w-full max-w-[450px] md:max-w-[820px] max-h-[680px] flex flex-col gap-8"
        header={
          <div className="py-3 px-8">
            <div className="flex items-center justify-between w-full">
              <Heading variant="h5">{entity}</Heading>
              <Button
                variant="quiet"
                size="icon"
                aria-label="Cerrar"
                onClick={() => {
                  setOpen(false);
                }}
                className="px-2 py-1.5 justify-center items-center gap-2 rounded-lg border border-neutral shadow-sm"
              >
                <Icon type="far" name="times" className="text-sm text-body" />
              </Button>
            </div>
          </div>
        }
        footer={
          <div className="w-full flex items-center justify-end gap-3 py-4 px-8">
            <Button
              variant="quiet"
              aria-label="Cerrar"
              onClick={() => {
                setOpen(false);
              }}
            >
              Cerrar
            </Button>
          </div>
        }
      >
        <div className="px-4">
          <TableData
            columns={columns}
            data={data || []}
            stripped
            wrapperClassName="overflow-x-auto"
          />
        </div>
      </ScrollModal>
    </>
  );
};
